import React, { useState, useEffect, useRef } from "react";
import "./BranchForm.scss";
import { useParams, useNavigate } from "react-router-dom";
import { addBranch, updateMembersManually, getBranchByBranchID } from "../../services/api/BranchApi";
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';

export const BranchForm = ({branchID:propBranchId ,onClose,onSuccess}) => {
  const { branchID:paramBranchID } = useParams();
  const branchID = propBranchId || paramBranchID;
  
  const { company, setCompany } = useAuth();

  const [formData, setFormData] = useState({
    branchName: "",
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Add refs for focus management
  const branchNameInputRef = useRef(null);
  const hasFocusedRef = useRef(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  const { showToast } = useToast();

  // Auto-focus effect
  useEffect(() => {
    // Prevent multiple focus attempts
    if (hasFocusedRef.current) return;

    // For editing mode, wait for initial data to load
    // For new branch, focus immediately after component mounts
    const shouldFocus = branchID ? initialDataLoaded : true;
    
    if (!shouldFocus) return;

    // Add a small delay to ensure DOM is ready
    const focusTimeout = setTimeout(() => {
      const branchNameInput = branchNameInputRef.current;
      if (branchNameInput && !hasFocusedRef.current) {
        branchNameInput.focus();
        
        // Move cursor to end of existing text (useful for edit mode)
        const val = branchNameInput.value || '';
        try {
          branchNameInput.setSelectionRange(val.length, val.length);
        } catch (e) {
          // Ignore selection range errors
        }
        
        hasFocusedRef.current = true;
      }
    }, 150);

    return () => clearTimeout(focusTimeout);
  }, [initialDataLoaded, branchID, formData.branchName]); // Add formData.branchName to ensure focus after data loads

  useEffect(() => {
    const fetchBranch = async () => {
      try {
        const res = await getBranchByBranchID(branchID);
        console.log(res, "aaaa")
        const branch = res?.data?.result?.branchData;
        if (branch) {
          setFormData({ branchName: branch.branchName || "" });
        }
        setInitialDataLoaded(true); // Mark as loaded
      } catch (err) {
        console.error("Failed to load branch data:", err);
        setInitialDataLoaded(true); // Mark as loaded even on error
      }
    };

    if (branchID) {
      fetchBranch();
    } else {
      // For new branch, mark as loaded immediately
      setInitialDataLoaded(true);
    }
  }, [branchID]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.branchName.trim()) errs.branchName = "Branch name is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Add form validation function
  const isFormValid = () => {
    // Check if branch name is filled and not just whitespace
    if (!formData.branchName || formData.branchName.trim() === '') return false;

    return true;
  };

  // Function to update company branches in localStorage
  const updateCompanyBranches = (newBranchId, isUpdate = false) => {
    if (!company) return;

    const updatedCompany = { ...company };
    
    if (isUpdate) {
      // For update, branches array remains the same
      // (assuming the branch ID doesn't change during update)
    } else {
      // For add, append the new branch ID to existing branches
      const existingBranches = company.branches || [];
      updatedCompany.branches = [...existingBranches, newBranchId];
    }

    // Update the company in context
    setCompany(updatedCompany);
    
    // Update localStorage
    localStorage.setItem('company', JSON.stringify(updatedCompany));
  };

  // Update the handleSubmit function
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid()) {
      showToast("Branch name is required", "error");
      return;
    }

    if (!validate()) return;

    if (isSaving) return; // Prevent multiple submissions
    setIsSaving(true);

    try {
      if (branchID) {
        const payload = { branchName: formData.branchName };
        const response = await updateMembersManually(branchID, payload);
        
        // Update company branches for update operation
        updateCompanyBranches(branchID, true);
        
        showToast("Branch updated successfully!", "success");
      } else {
        const response = await addBranch(formData);
        
        // Get the new branch ID from the response and update company branches
        const newBranchId = response?.data?.result?._id || response?.data?._id;
        if (newBranchId) {
          updateCompanyBranches(newBranchId, false);
        }
        
        // Reset form data and focus for new branches
        setFormData({ branchName: "" });
        hasFocusedRef.current = false; // Reset focus ref for new branches
        
        showToast("Branch added successfully!", "success");
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      showToast("Failed to submit branch: " , "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Enhanced Enter key submission handler
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      const target = e.target;
      const tagName = target.tagName.toLowerCase();
      const type = target.type;

      // Don't submit from textareas or when focused on buttons
      if (tagName === "textarea" || type === "button" || type === "submit") {
        return;
      }

      e.preventDefault();
      
      // Only submit if form is valid and not already saving
      if (isFormValid() && !isSaving) {
        handleSubmit(e);
      }
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      <div className="add-branch-form">
        <form 
          className="branch-form-user-form" 
          onSubmit={handleSubmit} 
          onKeyDown={handleKeyDown}
        >
          <h2>{branchID ? "Edit Branch Name" : "Add Branch Name"}</h2>
          <button
            type="button"
            className="branch-form-close-button"
            onClick={handleCancel}
            aria-label="Close"
          >
            &times;
          </button>
          <label>
            <input
              ref={branchNameInputRef}  // Add the ref here
              type="text"
              name="branchName"
              value={formData.branchName}
              onChange={handleChange}
              placeholder="Enter branch name"  // Add placeholder for better UX
            />
            {/* {errors.branchName && <span className="error">{errors.branchName}</span>} */}
          </label>

          <div className="button-group">
            <button
              type="submit"
              className="save-btn"
              disabled={!isFormValid() || isSaving}
            >
              {isSaving ? (branchID ? 'Updating...' : 'Saving...') : (branchID ? 'Update' : 'Save')}
            </button>
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </>
  );
};